// Relatório para o contador (E8), por caixa ou por competência (a escolha da
// empresa, em Conta e empresa → Relatório para o contador):
//   competência: as cargas carregadas no período e as despesas com data nele
//                (a compra a prazo entra inteira, na data da compra)
//   caixa:       o que entrou (adiantamentos e saldos recebidos) e o que saiu
//                (despesa à vista na data dela; a prazo, cada parcela na data
//                do pagamento); as retenções dos fretes entram quando o frete
//                é quitado
// Mais a remuneração dos colaboradores (competência: o que ganharam nos
// acertos, no histórico e pela folha; caixa: vales e pagamentos − devoluções,
// sem o reembolso das despesas que eles pagaram, que já estão nas despesas)
// e os agenciamentos pagos a transportadoras.
import { liquidoDoFrete, saldoDoFrete } from './fretes.js';
import { ganhosDaPessoa } from './ganhos.js';

const centavos = (v) => Math.round((Number(v) || 0) * 100);
const reais = (c) => c / 100;
const vivo = (x) => x && !x.deleted_at;
const porData = (a, b) => (a.data < b.data ? -1 : a.data > b.data ? 1 : 0);

export const REGIMES = {
  competencia: 'Competência: pela data da carga e da despesa',
  caixa: 'Caixa: pela data em que o dinheiro entrou ou saiu',
};

// O dia em que o frete foi quitado (o último recebimento): o do saldo, ou o
// do adiantamento quando não sobrou saldo.
export function quitadoEm(f) {
  if (centavos(saldoDoFrete(f)) > 0) return f.saldo_recebido_em ?? null;
  return f.adto_recebido_em ?? f.saldo_recebido_em ?? null;
}

function rotaDoFrete(f) {
  const lugar = (cidade, uf) => [cidade, uf].filter(Boolean).join('/');
  const de = lugar(f.origem_cidade, f.origem_uf);
  const para = lugar(f.destino_cidade, f.destino_uf);
  return de || para ? `${de || '?'} → ${para || '?'}` : '';
}

// `tabelas`: as da empresa (viagens, fretes, lancamentos, parcelas, anexos,
// categorias, veiculos, clientes, colaboradores, regras).
// `contas`: { regraValendo, creditosDaViagem, agenciamentosPendentes } de lib/acerto.js.
export function relatorioDoContador(tabelas, { de, ate, hoje, regime = 'competencia' }, contas) {
  const noPeriodo = (dia) => Boolean(dia) && dia >= de && dia <= ate;
  const viagens = new Map((tabelas.viagens ?? []).filter((v) => vivo(v) && v.status !== 'cancelada').map((v) => [v.id, v]));
  const fretes = (tabelas.fretes ?? []).filter((f) => vivo(f) && viagens.has(f.viagem_id));
  const clientes = new Map((tabelas.clientes ?? []).map((c) => [c.id, c]));
  const categorias = new Map((tabelas.categorias ?? []).map((c) => [c.id, c]));
  const veiculos = new Map((tabelas.veiculos ?? []).map((v) => [v.id, v]));
  const lancamentos = (tabelas.lancamentos ?? []).filter(vivo);
  const despesasPorId = new Map(lancamentos.filter((l) => l.tipo === 'despesa').map((l) => [l.id, l]));
  const anexos = (tabelas.anexos ?? []).filter(vivo);
  const contarAnexos = (campo, id) => anexos.filter((a) => a[campo] === id).length;
  const caixa = regime === 'caixa';

  // --- Fretes ------------------------------------------------------------
  const cargas = fretes
    .filter((f) => noPeriodo(caixa ? quitadoEm(f) : f.data_carregamento))
    .map((f) => {
      const cliente = clientes.get(f.cliente_id) ?? null;
      return {
        frete: f,
        data: caixa ? quitadoEm(f) : f.data_carregamento,
        carregamento: f.data_carregamento,
        cliente: cliente?.nome ?? '',
        documentoCliente: cliente?.documento ?? null,
        cte: f.cte_numero ?? null,
        rota: rotaDoFrete(f),
        bruto: Number(f.valor_frete) || 0,
        pedagio: f.pedagio_modo === 'embutido' ? Number(f.pedagio_valor) || 0 : 0,
        estadia: Number(f.estadia_valor) || 0,
        inss: Number(f.desc_inss) || 0,
        sest: Number(f.desc_sest_senat) || 0,
        irrf: Number(f.desc_irrf) || 0,
        outros: Number(f.desc_outros) || 0,
        liquido: reais(centavos(liquidoDoFrete(f)) + centavos(f.estadia_valor)),
        anexos: contarAnexos('frete_id', f.id),
      };
    })
    .sort(porData);
  const somaCargas = (campo) => reais(cargas.reduce((s, c) => s + centavos(c[campo]), 0));

  // Recebimentos (caixa): cada adiantamento e cada saldo recebido no período
  const recebimentos = [];
  if (caixa) {
    for (const f of fretes) {
      const cliente = clientes.get(f.cliente_id)?.nome ?? '';
      if (centavos(f.adto_valor) > 0 && noPeriodo(f.adto_recebido_em)) {
        recebimentos.push({ data: f.adto_recebido_em, tipo: 'adiantamento', frete: f, cliente, valor: Number(f.adto_valor) });
      }
      const saldo = saldoDoFrete(f);
      if (centavos(saldo) > 0 && noPeriodo(f.saldo_recebido_em)) {
        recebimentos.push({ data: f.saldo_recebido_em, tipo: 'saldo', frete: f, cliente, valor: saldo });
      }
    }
    recebimentos.sort(porData);
  }

  // --- Despesas ----------------------------------------------------------
  const linhaDespesa = (l, extra) => {
    const cat = categorias.get(l.categoria_id);
    const veiculo = veiculos.get(l.veiculo_id);
    return {
      lancamento: l,
      categoria: cat?.nome ?? 'Outros',
      ordem: cat?.ordem ?? 999,
      veiculo: veiculo?.placa ?? null,
      fornecedor: l.fornecedor ?? null,
      nota: l.documento_numero ?? null,
      descricao: l.descricao ?? null,
      forma: l.forma ?? null,
      pagoPor: l.pago_por ?? 'empresa',
      anexos: contarAnexos('lancamento_id', l.id),
      ...extra,
    };
  };
  const despesas = [];
  if (caixa) {
    for (const l of despesasPorId.values()) {
      if (l.condicao !== 'a_prazo' && noPeriodo(l.data)) despesas.push(linhaDespesa(l, { data: l.data, valor: Number(l.valor), parcela: null }));
    }
    for (const p of (tabelas.parcelas ?? []).filter(vivo)) {
      const l = despesasPorId.get(p.lancamento_id);
      if (l && l.condicao === 'a_prazo' && noPeriodo(p.pago_em)) {
        despesas.push(linhaDespesa(l, { data: p.pago_em, valor: Number(p.valor), parcela: `${p.numero}/${p.total}` }));
      }
    }
  } else {
    for (const l of despesasPorId.values()) {
      if (noPeriodo(l.data)) despesas.push(linhaDespesa(l, { data: l.data, valor: Number(l.valor), parcela: l.condicao === 'a_prazo' ? 'a prazo' : null }));
    }
  }
  despesas.sort((a, b) => porData(a, b) || a.ordem - b.ordem);
  const categoriasDoPeriodo = new Map();
  for (const d of despesas) {
    const c = categoriasDoPeriodo.get(d.categoria) ?? { categoria: d.categoria, ordem: d.ordem, quantidade: 0, total: 0 };
    c.quantidade += 1;
    c.total += centavos(d.valor);
    categoriasDoPeriodo.set(d.categoria, c);
  }
  const porCategoria = [...categoriasDoPeriodo.values()]
    .sort((a, b) => a.ordem - b.ordem || a.categoria.localeCompare(b.categoria, 'pt-BR'))
    .map((c) => ({ categoria: c.categoria, quantidade: c.quantidade, total: reais(c.total) }));

  // --- Remuneração dos colaboradores --------------------------------------
  const remuneracao = [];
  for (const c of (tabelas.colaboradores ?? []).filter(vivo)) {
    const g = ganhosDaPessoa(c.id, tabelas, { de, ate, hoje }, contas);
    const valor = caixa ? g.recebido.liquido : g.totais.ganho;
    if (centavos(valor) !== 0 || (caixa && centavos(g.recebido.total) !== 0)) {
      remuneracao.push({ colaborador: c, valor, pago: g.recebido.total, reembolso: g.recebido.despesas });
    }
  }

  // --- Agenciamentos pagos a transportadoras -------------------------------
  const agenciamentos = fretes
    .filter((f) => f.agenciado_por === 'transportadora' && centavos(f.agenciamento_valor) > 0
      && noPeriodo(caixa ? f.agenciamento_pago_em : f.data_carregamento))
    .map((f) => ({
      data: caixa ? f.agenciamento_pago_em : f.data_carregamento,
      frete: f,
      cliente: clientes.get(f.cliente_id)?.nome ?? '',
      valor: Number(f.agenciamento_valor),
      pago: Boolean(f.agenciamento_pago_em),
    }))
    .sort(porData);

  const soma = (lista) => reais(lista.reduce((s, x) => s + centavos(x.valor), 0));
  // Parte do período veio dos programas antigos (sem os pagamentos das comissões)
  const historico = lancamentos.some((l) => l.origem === 'importacao' && noPeriodo(l.data));
  return {
    regime,
    historico,
    de,
    ate,
    cargas,
    totaisCargas: {
      quantidade: cargas.length,
      bruto: somaCargas('bruto'),
      pedagio: somaCargas('pedagio'),
      estadia: somaCargas('estadia'),
      inss: somaCargas('inss'),
      sest: somaCargas('sest'),
      irrf: somaCargas('irrf'),
      outros: somaCargas('outros'),
      liquido: somaCargas('liquido'),
    },
    recebimentos,
    totalRecebido: soma(recebimentos),
    despesas,
    porCategoria,
    totalDespesas: soma(despesas),
    remuneracao,
    totalRemuneracao: soma(remuneracao),
    agenciamentos,
    totalAgenciamentos: soma(agenciamentos),
  };
}

// Os anexos (fotos e PDFs) das despesas e das cargas do relatório, para o .zip.
export function anexosDoRelatorio(r, anexos) {
  const despesas = new Map(r.despesas.map((d) => [d.lancamento.id, d]));
  const cargas = new Map(r.cargas.map((c) => [c.frete.id, c]));
  const lista = [];
  for (const a of anexos.filter(vivo)) {
    if (a.lancamento_id && despesas.has(a.lancamento_id)) lista.push({ anexo: a, despesa: despesas.get(a.lancamento_id) });
    else if (a.frete_id && cargas.has(a.frete_id)) lista.push({ anexo: a, carga: cargas.get(a.frete_id) });
  }
  return lista;
}
